import { Divider, Stack, useTheme } from '@mui/material'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { useEffect, useState } from 'react'
import {
  INVITED_LIST_URL,
  ORGANIZATIONS_URL,
  USERS_URL,
  COHORTS_URL,
  FEATURES_URL,
  FEATURE_STATUSES
} from 'services/constants'
import { getRequest } from 'utils/api'
import CompanyInvitedUserList from 'components/molecules/adminDashboard/companyUsers/CompanyInvitedUserList'
import {
  getGoalNameById,
  getOrgLicenseStatus,
  userRoleMenuItems,
  getUserCohortPaths,
  getGoals,
  getUserLicensePaths
} from 'utils/getSupportingInfo'
import { getUserOrganizationId } from 'utils/localStorage'
import { permitted } from 'utils/permissionCheck'
import { PERMISSION_LEVEL } from 'routes/constants'
import { useTranslation } from 'react-i18next'
import DeactivatedUserList from 'components/molecules/adminDashboard/companyUsers/DeactivatedUserList'
import LicensedUsersList from 'components/molecules/adminDashboard/companyUsers/LicensedUsersList'
import RaterOnlyUsersList from 'components/molecules/adminDashboard/companyUsers/RaterOnlyUsersList'

const CompanyUsersCard = ({ organizationId, refreshDataRef }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [users, setUsers] = useState({})
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [requestError, setRequestError] = useState(false)
  const [invitedUsers, setInvitedUsers] = useState([])
  const [inviteRequestError, setInviteRequestError] = useState(false)
  const [licenseActive, setLicenseActive] = useState(null)
  const [deactivatedUsers, setDeactivatedUsers] = useState([])
  const [deactivatedRequestError, setDeactivatedRequestError] = useState(false)

  const isSuperAdminView =
    organizationId && permitted(PERMISSION_LEVEL.SUPER_ADMIN)
  const [features, setFeatures] = useState([])

  const fetchInviteData = async () => {
    const res = await getRequest(INVITED_LIST_URL, {
      organizationId: isSuperAdminView
        ? organizationId
        : getUserOrganizationId()
    })
    let invited = []
    if (res.status === 200) {
      if (res.data?.data?.length > 0) {
        const licenseInvite = res.data.data
        const roleList = userRoleMenuItems()
        invited = await Promise.all(
          licenseInvite.map(async u => {
            const user = u
            if (u.goalId) {
              user.goalName = await getGoalNameById(u.goalId)
            }
            if (u.userRole) {
              const roleItem = roleList.find(r => r.value === u.userRole)
              user.roleName = t(`company.${roleItem?.label}`)
            }
            return user
          })
        )
      }
      setInviteRequestError(false)
    } else {
      setInviteRequestError(true)
    }
    setInvitedUsers(invited)
  }

  const fetchCohorts = async () => {
    const res = await getRequest(COHORTS_URL, {
      organizationId: organizationId || getUserOrganizationId()
    })
    if (res.status === 200) {
      return res.data.data
    }
  }
  const fetchLicense = async orgUsers => {
    const newOrgUsers = { ...orgUsers }
    const resOrg = await getRequest(
      `${ORGANIZATIONS_URL}/${organizationId || getUserOrganizationId()}`,
      {
        licenseStats: true
      }
    )
    let licenseUsers = []
    if (resOrg.status === 200) {
      const orgData = resOrg.data.data
      licenseUsers = orgData.users
      const orgActive = getOrgLicenseStatus(orgData)
      if (orgActive === false) {
        orgData.licenseStats.licensesAvailable = 0
        orgData.licenseStats.licensesUsed = 0
        orgData.licenseCount = 0
      }
      setLicenseActive(orgActive)
      setOrganization(orgData)
    } else {
      setRequestError(true)
    }

    const cohorts = await fetchCohorts()
    const goals = await getGoals()
    licenseUsers.forEach(u => {
      if (!newOrgUsers[u.id]) {
        newOrgUsers[u.id] = u
      }
      newOrgUsers[u.id].userLicense = u.userLicense
      newOrgUsers[u.id].paths = getUserCohortPaths(u, cohorts, goals)
      newOrgUsers[u.id].licensedPaths = getUserLicensePaths(
        u.userLicense?.goalIds,
        goals,
        newOrgUsers[u.id].paths
      )
    })
    setUsers(newOrgUsers)
  }
  const fetchDeactivatedData = async () => {
    const res = await getRequest(USERS_URL, {
      noStats: true,
      deleted: true,
      organizationId
    })
    let resData = []
    if (res.status === 200) {
      if (res.data?.data?.length > 0) resData = res.data.data
      setDeactivatedRequestError(false)
    } else {
      setDeactivatedRequestError(true)
    }
    setDeactivatedUsers(resData)
  }

  const fetchUsers = async () => {
    const queryParams = {
      noStats: true
    }
    if (isSuperAdminView) {
      queryParams.organizationId = organizationId
    }
    const resUser = await getRequest(USERS_URL, queryParams)
    const orgUsers = {}
    if (resUser.status === 200) {
      const resUsers = resUser.data?.data
      resUsers?.forEach(u => {
        orgUsers[u.id] = u
      })
    } else {
      setRequestError(true)
    }
    return orgUsers
  }
  useEffect(() => {
    const fetchData = async () => {
      const orgUsers = await fetchUsers()

      if (isSuperAdminView) {
        const featureRes = await getRequest(FEATURES_URL)
        if (featureRes.status === 200) {
          const data = featureRes.data.data.filter(
            f =>
              f.status === FEATURE_STATUSES.BETA ||
              f.status === FEATURE_STATUSES.PRODUCTION_PAID
          )
          setFeatures(data)
        }
      }
      await Promise.all([
        fetchDeactivatedData(),
        fetchLicense(orgUsers),
        fetchInviteData()
      ])
      setLoading(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, refreshDataRef])

  const deleteUserCallback = async userId => {
    delete users[userId]
    await fetchDeactivatedData()
  }

  const downgradeUserCallback = userId => {
    const orgUsers = { ...users }
    delete orgUsers[userId].userLicense
    setUsers(orgUsers)
    organization.licenseStats.licensesUsed--
    organization.licenseStats.licensesAvailable++
  }

  const inviteUserCallback = async () => {
    await fetchLicense(users)
    await fetchInviteData()
  }

  const removeInvitedUser = id => {
    setInvitedUsers(invitedUsers.filter(i => i.id !== id))
    organization.licenseStats.licensesUsed--
    organization.licenseStats.licensesAvailable++
  }

  const reactivateCallback = async (userId) => {
    const orgUsers = await fetchUsers()
    await fetchLicense(orgUsers)

    const newDeactivatedUsers = deactivatedUsers.filter(u => u.id !== userId)
    setDeactivatedUsers(newDeactivatedUsers)
  }

  const contentContainerProps = {
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    padding: 5,
    spacing: 3
  }
  return (
    <Stack spacing={5} padding={0} sx={{ paddingBottom: 20 }}>
      <LicensedUsersList
        users={users}
        organization={organization}
        requestError={requestError}
        inviteUserCallback={inviteUserCallback}
        isSuperAdminView={isSuperAdminView}
        contentContainerProps={contentContainerProps}
        licenseActive={licenseActive}
        downgradeUserCallback={downgradeUserCallback}
        deleteUserCallback={deleteUserCallback}
        features={features}
      />
      <Divider />
      <RaterOnlyUsersList
        users={users}
        requestError={requestError}
        organization={organization}
        inviteUserCallback={inviteUserCallback}
        isSuperAdminView={isSuperAdminView}
        contentContainerProps={contentContainerProps}
        licenseActive={licenseActive}
        features={features}
        fetchLicense={fetchLicense}
        deleteUserCallback={deleteUserCallback}
      />
      <Divider />
      <CompanyInvitedUserList
        users={invitedUsers}
        requestError={inviteRequestError}
        removeCallback={removeInvitedUser}
        licenseActive={licenseActive}
      />
      <DeactivatedUserList
        users={deactivatedUsers}
        requestError={deactivatedRequestError}
        reactivateCallback={reactivateCallback}
        licenseAvailable={
          licenseActive && organization?.licenseStats?.licensesAvailable > 0
        }
        isSuperAdminView={isSuperAdminView}
      />
      <OverlayLoading open={loading} />
    </Stack>
  )
}

export default CompanyUsersCard
